import React, { useState, useCallback } from 'react';
import {
  Typography,
  CircularProgress,
  IconButton,
  styled,
  InputBase,
  Button,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box } from '@mui/system';
import axios from 'axios';
import {
  SET_MESSAGE,
  SET_NEW_MESSAGE,
  url,
} from '../../Helpers/utils/Constant';
import { IConversation } from '../../Types/ConversationTypes/ConversationTypes';
import { useEffect } from 'react';
import ShowConvMessage from './ShowConvMessage';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';

interface IConvoBox {
  setSupportShow: React.Dispatch<React.SetStateAction<boolean>>;
  conversation: IConversation | undefined;
  userId: number;
}
const MessageInputWrapper = styled(InputBase)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(14)};
    width: 100%;
    `
);
const ConvoBox = ({ setSupportShow, conversation, userId }: IConvoBox) => {
  const { dispatch, messages } = useAuthContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [files, setFiles] = useState<any>([]);

  const setRef = useCallback((node: any) => {
    if (node) {
      node.scrollIntoView({ smooth: true });
    }
  }, []);

  useEffect(() => {
    if (conversation?.support_conversation_id && !messages.length) {
      setLoading(true);
      axios
        .get(
          `${url}/api/atab/support-conversation/get/all/message/conversation/${conversation.support_conversation_id}`
        )
        .then(function (res) {
          if (res.data.success) {
            dispatch?.({ type: SET_MESSAGE, payload: res.data.data });
            setLoading(false);
          } else {
            setLoading(false);
          }
        })
        .catch(function (err) {
          if (err.response.data.message) {
            setLoading(false);
          }
        });
    }
  }, [conversation?.support_conversation_id, dispatch, messages.length]);

  const handelSendMessage = () => {
    const formData = new FormData();
    if (conversation?.support_conversation_id) {
      formData.append(
        'support_conversation_msg_conv_id',
        conversation?.support_conversation_id.toString()
      );
    }
    if (message) {
      formData.append('support_conversation_msg_text', message);
    }
    if (files.length) {
      files.forEach((file: any, index: number) => {
        const name = 'support_conversation_msg_atchment' + index + 1;
        formData.append(name, file);
      });
    }
    formData.append('support_conversation_msg_sender', 'member');
    formData.append('support_conversation_sender_member_id', userId.toString());
    axios
      .post(`${url}/api/atab/support-conversation/send-message`, formData)
      .then(function (res) {
        if (res.data.success) {
          const newMessage: any = {
            support_conversation_msg_text: message,
            support_conversation_msg_conv_id:
              conversation?.support_conversation_id,
            support_conversation_msg_text_time:
              res.data.data.support_conversation_msg_text_time,
            support_conversation_msg_id:
              res.data.data.support_conversation_msg_id,
            support_conversation_msg_atchments:
              res.data.data.support_conversation_msg_atchments,
            support_conversation_sender_member_id: userId,
          };
          dispatch?.({ type: SET_NEW_MESSAGE, payload: newMessage });
          setMessage('');
          setFiles([]);
        }
      })
      .catch(function (err) { });
  };

  return (
    <Box bgcolor='#ffff' height={450} width={310} border={1}>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        p={1}
        bgcolor='#5E60FF'
      >
        <Box display='flex' alignItems='center' gap={1}>
          <img
            src='https://embed.tawk.to/_s/v4/assets/images/default-profile.svg'
            alt='Atab Admin'
            width={40}
          />
          <Typography variant='body2' color='white'>
            ATAB Admin
          </Typography>
        </Box>
        <Box>
          <HighlightOffIcon
            onClick={() => setSupportShow(false)}
            sx={{ color: 'white', cursor: 'pointer' }}
          />
        </Box>
      </Box>
      <Box className={messages?.length > 5 ? 'show-message-box' : ''}>
        {loading ? (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {messages?.length ? (
              <Box>
                {messages.map((sMessage, index: number) => {
                  const lastMessage = messages.length - 1 === index;

                  return (
                    <Box
                      ref={lastMessage ? setRef : null}
                      key={sMessage.support_conversation_msg_id}
                    >
                      <ShowConvMessage sMessage={sMessage} />
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <>
                <Typography
                  variant='body1'
                  gutterBottom
                  textAlign={'center'}
                  pt={10}
                >
                  No message yet
                </Typography>
              </>
            )}
          </>
        )}
      </Box>{' '}
      <Box
        sx={{
          position: 'absolute',
          bottom: 2,
          width: '307px',
          background: '#F2F5F9',
          overflow: 'hidden',
        }}
      >
        {files.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 1,
              overflowX: 'auto',
              borderBottom: '1px solid #e2e8f0',
              maxHeight: '65px',
              alignItems: 'center',
              backgroundColor: '#ffffff',
            }}
          >
            {files.map((file: any, index: number) => {
              const isPdf = file.name.toLowerCase().endsWith('.pdf');
              const isImage = file.type.startsWith('image/');
              return (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    pl: 1,
                    pr: 3.5,
                    py: 0.25,
                    maxWidth: '120px',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                    {isPdf ? '📄' : isImage ? '🖼️' : '📁'}
                  </span>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      color: '#475569',
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFiles((prev: any) => prev.filter((_: any, i: number) => i !== index));
                    }}
                    sx={{
                      position: 'absolute',
                      right: 2,
                      p: 0.25,
                      color: '#94a3b8',
                      '&:hover': {
                        color: '#ef4444',
                        bgcolor: '#fee2e2',
                      },
                    }}
                  >
                    <HighlightOffIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
        <Box className='input-field-and-button'>
          <IconButton
            color='primary'
            aria-label='upload file'
            component='label'
            sx={{ p: 0.5 }}
          >
            <input
              hidden
              type="file"
              multiple
              onChange={(e: any) => {
                if (e.target.files) {
                  const newFiles = Array.from(e.target.files);
                  setFiles((prev: any) => [...prev, ...newFiles]);
                }
              }}
            />
            <DriveFolderUploadIcon
              sx={{ fontSize: '30px', color: '#5e60ff' }}
            />
          </IconButton>
          <MessageInputWrapper
            autoFocus
            placeholder='Write your message here...'
            fullWidth
            value={message}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMessage(e.target.value)
            }
            multiline
          />
          {message || files.length ? (
            <Button
              sx={{ background: 'rgba(12, 77, 162, 0.1)' }}
              onClick={handelSendMessage}
            >
              Send
            </Button>
          ) : (
            ''
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ConvoBox;
